import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { toast } from "sonner";

function ResetPassword() {
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [isValidSession, setIsValidSession] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    // Supabase handles the token automatically via URL hash
    // When user clicks email link, Supabase sets up a recovery session
    const checkSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session) {
          setIsValidSession(true);
        } else {
          // Check for hash params (Supabase puts recovery tokens in URL hash)
          const hashParams = new URLSearchParams(window.location.hash.substring(1));
          const accessToken = hashParams.get('access_token');
          const type = hashParams.get('type');
          
          if (accessToken && type === 'recovery') {
            // Set the session from the recovery token
            const { error } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: hashParams.get('refresh_token') || '',
            });
            
            if (!error) {
              setIsValidSession(true);
            } else {
              toast.error("Invalid or expired reset link. Please request a new one.");
              navigate("/ForgotPassword");
            }
          } else {
            toast.error("Invalid reset link. Please request a new one.");
            navigate("/ForgotPassword");
          }
        }
      } catch (error) {
        console.error("Session check error:", error);
        toast.error("Something went wrong. Please try again.");
        navigate("/ForgotPassword");
      } finally {
        setChecking(false);
      }
    };
    
    checkSession();
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });
      
      if (error) {
        throw error;
      }
      
      setSuccess(true);
      toast.success("Password reset successfully!");
      
      // Sign out after password reset so they can log in fresh
      await supabase.auth.signOut();
    } catch (err) {
      toast.error(err?.message || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0b0b0b]">
        <div className="text-[#808080]">Verifying reset link...</div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0b0b0b] px-4">
        <Card className="w-full max-w-md bg-[#121212] text-[#e0e0e0] border border-[#1f1f1f]">
          <CardHeader>
            <CardTitle className="text-center text-2xl">Password Reset Complete</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-center text-[#a0a0a0]">
              Your password has been reset successfully. You can now sign in with your new password.
            </p>
            <Button
              type="button"
              className="w-full"
              onClick={() => navigate("/Auth")}
            >
              Sign In
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!isValidSession) {
    return null; // Will redirect in useEffect
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0b0b0b] px-4">
      <Card className="w-full max-w-md bg-[#121212] text-[#e0e0e0] border border-[#1f1f1f]">
        <CardHeader>
          <CardTitle className="text-center text-2xl">Set New Password</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-[#a0a0a0] mb-4 text-center">
            Enter your new password below.
          </p>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div>
              <label className="text-sm text-[#a0a0a0]">New Password</label>
              <Input
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                type="password"
                required
                minLength={6}
                placeholder="At least 6 characters"
                className="bg-[#0f0f0f] border-[#1f1f1f] text-[#e0e0e0]"
              />
            </div>
            <div>
              <label className="text-sm text-[#a0a0a0]">Confirm Password</label>
              <Input
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                type="password"
                required
                minLength={6}
                placeholder="Confirm your password"
                className="bg-[#0f0f0f] border-[#1f1f1f] text-[#e0e0e0]"
              />
            </div>
            <Button type="submit" disabled={loading} className="w-full">
              {loading ? "Resetting..." : "Reset Password"}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => navigate("/Auth")}
            >
              Back to Sign In
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default ResetPassword;
